import { ReactElement, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { StatusBar, PrimaryButton, Typography } from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import styles from './MenuModal.module.scss';
import { useNavigate, useLocation } from 'react-router-dom';
import { IFormData, IMenuModal, TVariant } from './types';
import { IDuplicateMenuRequest, IMenuRequest } from '../../api/types';
import { duplicateMenuOnSubmit } from './duplicateMenuOnSubmit/duplicateMenuOnSubmit';
import { createAndUpdateMenuOnSubmit } from './createAndUpdateMenuOnSubmit/createAndUpdateMenuOnSubmit';
import { QueryKeys } from '../../hooks/QueryKeys';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { useRanges } from 'src/hooks/useRanges/useRanges';
import { useMenuLabels } from 'src/hooks/useMenuLabels';
import { getMenuLabelIds, sameMenuLabels, toMenuLabelsPayload } from 'src/utils/menuLabels/menuLabels';
import { IMenuDetailsFieldsLabels, MenuDetailsFields } from '../MenuDetailsFields';
import { FormVariant } from '../../types/menuItemForm.types';

const MENU_DETAILS_LABELS: IMenuDetailsFieldsLabels = {
  name: 'Menu name',
  nameRequired: 'Please enter a menu name',
  consumerName: 'Consumer name',
  consumerNameHelperText: 'Name seen when the menu is published',
  image: 'Image',
};

const MenuModalContent = ({
  menuName,
  title,
  closeModal,
  variant,
  id,
  consumerName,
  image,
  menuLabels,
}: IMenuModal): ReactElement => {
  const queryClient = useQueryClient();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { trackEvent, captureException } = useUserTracking();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const [uploadedImage, setUploadedImage] = useState<File | string | null>(image);
  const isImageObject = typeof uploadedImage === 'object';
  const { data: ranges } = useRanges({ enabled: variant === 'create' });

  const isDuplicate = variant === 'duplicate';
  // Labels aren't copied when duplicating a menu.
  const { data: brandMenuLabels, isLoading: isMenuLabelsLoading } = useMenuLabels({ enabled: !isDuplicate });
  const savedMenuLabelIds = variant === FormVariant.UPDATE ? getMenuLabelIds(menuLabels ?? []) : [];

  const buttonTextLookup: Record<TVariant, string> = {
    create: 'Create',
    update: 'Save',
    duplicate: 'Duplicate',
  };

  const defaultValues: IFormData = {
    name: (isDuplicate ? `${menuName} (Copy)` : menuName) || '',
    consumerName: consumerName || '',
    files: uploadedImage,
    source: 'library',
    ranges: [],
    menuLabels: savedMenuLabelIds,
  };

  const form = useForm<IFormData>({
    defaultValues,
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
    watch,
    setError,
    clearErrors,
  } = form;
  const menuSource = watch('source');

  useEffect(() => {
    if (menuSource === 'library') {
      clearErrors('ranges');
    }
  }, [menuSource, clearErrors]);

  const onSubmit = async (data: IFormData) => {
    clearError();
    if (menuSource === 'ranges' && (!data.ranges || data.ranges.length === 0)) {
      setError('ranges', { message: 'Must select one or more ranges before you can create your menu' });
      return;
    }

    const payload: IMenuRequest = {
      name: data.name,
      consumer_name: data.consumerName,
    };

    // On update, leaving menu_labels out keeps the saved labels.
    if (!sameMenuLabels(data.menuLabels, savedMenuLabelIds)) {
      payload.menu_labels = toMenuLabelsPayload(data.menuLabels);
    }

    const payloadCreateOrUpdate =
      variant === FormVariant.UPDATE
        ? payload
        : {
            ...payload,
            ranges: JSON.stringify(data.ranges),
          };

    const payloadDuplicate: IDuplicateMenuRequest = {
      name: data.name,
      consumer_name: data.consumerName,
    };

    if (uploadedImage && isImageObject) {
      payloadCreateOrUpdate.files = uploadedImage;
      payloadDuplicate.files = uploadedImage;
    }
    try {
      isDuplicate
        ? await duplicateMenuOnSubmit(id, payloadDuplicate, closeModal, showNotification, navigate, pathname)
        : await createAndUpdateMenuOnSubmit(variant, closeModal, id, payloadCreateOrUpdate, showNotification);

      const eventMap = { create: 'menu_created', update: 'menu_updated', duplicate: 'menu_duplicated' } as const;
      trackEvent(eventMap[variant], { menuName: data.name, variant });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QueryKeys.menus] }),
        variant === FormVariant.UPDATE && queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, id] }),
        payload.menu_labels && queryClient.invalidateQueries({ queryKey: [QueryKeys.menuLabels] }),
      ]);
    } catch (e) {
      captureException(e, { tags: { action: `menu_${variant}`, menuName: data.name } });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Typography size="xxlarge" weight="semi-bold" className={styles['title']}>
        {title}
      </Typography>

      <div className={styles['text-inputs']}>
        <MenuDetailsFields
          form={form}
          labels={MENU_DETAILS_LABELS}
          image={uploadedImage}
          setImage={setUploadedImage}
          showSource={variant === FormVariant.CREATE}
          ranges={ranges}
          showMenuLabels={!isDuplicate}
          menuLabels={brandMenuLabels}
          isMenuLabelsLoading={isMenuLabelsLoading}
        />
      </div>

      <PrimaryButton data-cy="create-button" loading={isSubmitting} size="full-width" type="submit">
        {buttonTextLookup[variant]}
      </PrimaryButton>
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </form>
  );
};

export default MenuModalContent;
